// Protected sheets and ranges (Data > Protect sheets and ranges). The server refuses changes to protected cells
// from users who are not allowed to edit them; the browser uses the same check to warn before sending.
import type { Op } from './ops.ts';
import type { Rng } from './props.ts';

export interface Protection extends Rng {
  id: string;
  sheet?: boolean;          // the whole sheet (then `except` lists unprotected ranges)
  except?: Rng[];
  editors: number[];        // user ids allowed to edit (the document owner always is)
  warn?: boolean;           // only show a warning, do not block
  desc?: string;
}
type SheetProps = (sheet: number) => Record<string, unknown> | undefined;

const hit = (p: Protection, R: Rng): boolean =>
  p.r1 <= R.r2 && p.r2 >= R.r1 && p.c1 <= R.c2 && p.c2 >= R.c1 &&
  // a range fully inside one of the sheet's exceptions is not protected
  !(p.except ?? []).some(e => R.r1 >= e.r1 && R.r2 <= e.r2 && R.c1 >= e.c1 && R.c2 <= e.c2);

// a protection saved without an editor list is editable by the owner only (it must not make every check throw)
export const protections = (props: Record<string, unknown> | undefined): Protection[] =>
  (Array.isArray(props?.protected) ? (props!.protected as Protection[]).filter(p => p && typeof p === 'object').map(p => (Array.isArray(p.editors) ? p : { ...p, editors: [] })) : []);
/** File > Security restrictions are the owner's: does `op` change them? */
export function touchesRestrictions(op: Op): boolean {
  if (op.t === 'batch') return op.ops.some(touchesRestrictions);
  return op.t === 'docprops' && 'restrictions' in op.patch;
}

/** The protections `op` runs into for this user: `blocked` ones refuse it, `warned` ones ask for confirmation. */
export function protectionHits(op: Op, sheetProps: SheetProps, userId: number, owner: boolean): { blocked: Protection[]; warned: Protection[] } {
  const blocked = new Set<Protection>(), warned = new Set<Protection>();
  if (owner) return { blocked: [], warned: [] };
  const touch = (sheet: number, R: Rng | null): void => {
    for (const p of protections(sheetProps(sheet))) {
      if (p.editors.includes(userId)) continue;
      if (R && !hit(p, R)) continue;
      if (!R && !p.sheet) continue;            // sheet-level changes only meet whole-sheet protection
      (p.warn ? warned : blocked).add(p);
    }
  };
  const visit = (o: Op): void => {
    switch (o.t) {
      case 'batch': o.ops.forEach(visit); return;
      case 'set': touch(o.sheet, { r1: o.r, c1: o.c, r2: o.r, c2: o.c }); return;
      case 'fmt': touch(o.sheet, o); return;
      case 'insert': case 'delete': {
        // deleting protected rows/columns, or inserting inside a protected block, changes protected cells
        const span = o.t === 'delete' ? [o.at, o.at + o.count - 1] : [o.at, o.at];
        for (const p of protections(sheetProps(o.sheet))) {
          if (p.editors.includes(userId)) continue;
          const [a, b] = o.axis === 'row' ? [p.r1, p.r2] : [p.c1, p.c2];
          const inside = o.t === 'delete' ? span[0] <= b && span[1] >= a : o.at > a && o.at <= b;
          if (inside || p.sheet) (p.warn ? warned : blocked).add(p);
        }
        return;
      }
      case 'props': {
        const old = protections(sheetProps(o.sheet));
        if ('protected' in o.patch) {
          // changing or removing a protection needs the right to edit it
          const next = new Map(protections(o.patch).map(p => [p.id, JSON.stringify(p)]));
          for (const p of old) if (!p.warn && !p.editors.includes(userId) && next.get(p.id) !== JSON.stringify(p)) blocked.add(p);
        }
        if (Object.keys(o.patch).some(k => k !== 'protected')) touch(o.sheet, null);
        return;
      }
      case 'sheet.rename': case 'sheet.move': touch(o.sheet, null); return;
      // deleting a sheet deletes every protected range on it too
      case 'sheet.delete':
        for (const p of protections(sheetProps(o.sheet))) if (!p.editors.includes(userId)) (p.warn ? warned : blocked).add(p);
        return;
      default: return;
    }
  };
  visit(op);
  return { blocked: [...blocked], warned: [...warned] };
}
